/**
 * A worker's mark: their craft glyph on the gradient the registry gives them,
 * falling back to their initial when the glyph map doesn't know the slug.
 *
 * Deliberately NOT a `ui/` primitive. `ui/avatar.tsx` is the design-system
 * atom for a person (an image with a text fallback); this is domain furniture
 * that renders two colour tokens off the wire, and putting it in `ui/` would
 * put a worker-shaped thing in the design system.
 *
 * Never a fetched image URL: a worker renders identically on every surface
 * without a fetch, which is why the glyphs are inline path data
 * (`workerGlyphs.tsx`) and `WorkerAvatar` stays two stops rather than a file.
 */
import type { CSSProperties } from "react";
import type { WorkerAvatar } from "@yagni/shared";
// The atom owns its styles. It was only ever mounted under the Workers surface,
// whose shell imported the stylesheet; the Work surfaces draw the same mark now
// (YAG-372), and an unstyled mark on /work is not a hypothetical.
import "./workers.css";
import { workerGlyph, workerGlyphById } from "./workerGlyphs.js";

/**
 * The craft as a stamped badge, tinted with the worker's own colour so the
 * role reads as part of the identity the mark already wears. The deeper
 * gradient stop anchors the tint; the CSS mixes it toward ink and paper so
 * the wire never has to carry a legible-text colour.
 */
export function RoleBadge({ craft, avatar }: { craft: string; avatar: WorkerAvatar }) {
  return (
    <span
      className="wrk-role-badge"
      style={{ "--wrk-tint": avatar.gradientTo } as CSSProperties}
    >
      {craft}
    </span>
  );
}

export function WorkerMark({
  avatar,
  name,
  slug,
  size = "lg",
}: {
  avatar: WorkerAvatar;
  name: string;
  /** Registry slug selecting the craft glyph; omit (or unknown) → initial. */
  slug?: string;
  size?: "xl" | "lg" | "sm";
}) {
  // The wire's glyph identifier wins (YAG-591 §2); the slug map is the fallback
  // for a response that predates it, and the initial is the fallback for both.
  const glyph = workerGlyphById(avatar.glyph) ?? (slug ? workerGlyph(slug) : null);
  return (
    <span
      aria-hidden
      className={`wrk-mark wrk-mark--${size}`}
      style={{
        backgroundImage: `linear-gradient(135deg, ${avatar.gradientFrom}, ${avatar.gradientTo})`,
      }}
    >
      {glyph ? (
        <svg
          viewBox="0 0 72 72"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {glyph}
        </svg>
      ) : (
        name.trim().slice(0, 1).toUpperCase()
      )}
    </span>
  );
}
